import type { FC } from "react";
import { usePlayBundledSounds } from "@/lib/audio/use-play-sound";
import { cn } from "@/lib/cn";
import {
  PLAYBACK_WARMUP_MS,
  RECORDING_ORANGE_START_RATIO,
} from "@/lib/constants";
import type { AudioAssetName } from "@/lib/data/audio-assets";

export const PlaySoundsButton: FC<{
  baseFileNames: readonly AudioAssetName[];
  label: string;
  playingLabel?: string;
  playbackDurationMs: number;
  onEnded?: () => void;
  nudge?: boolean;
  dim?: boolean;
  className?: string;
}> = ({
  baseFileNames,
  label,
  playingLabel,
  playbackDurationMs,
  onEnded,
  nudge,
  dim,
  className,
}) => {
  const [play, playing] = usePlayBundledSounds(baseFileNames);
  const currentLabel = playing ? (playingLabel ?? label) : label;

  return (
    <button
      type="button"
      onClick={() => play(onEnded)}
      disabled={playing}
      aria-label={currentLabel}
      className={cn(
        "relative flex w-full select-none items-center justify-center gap-3 overflow-hidden rounded-full border border-neutral-950 bg-white px-8 py-5 font-bold text-lg text-neutral-950 shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50",
        playing
          ? "playback-progress-live text-white"
          : "hover:bg-neutral-50 active:bg-neutral-100",
        nudge && "replay-nudge",
        dim && "opacity-50",
        className,
      )}
      style={{
        ["--playback-audio-duration" as string]: `${playbackDurationMs}ms`,
        ["--playback-warmup-duration" as string]: `${PLAYBACK_WARMUP_MS}ms`,
        ["--playback-orange-transition" as string]: `${Math.round(PLAYBACK_WARMUP_MS * (1 - RECORDING_ORANGE_START_RATIO))}ms`,
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.12),rgba(255,255,255,0))] opacity-60"
      />
      <span
        aria-hidden="true"
        className={cn(
          "playback-warmup-layer pointer-events-none absolute inset-0 rounded-full bg-[#6a4b32]",
          playing ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "playback-progress-base pointer-events-none absolute inset-0 rounded-full",
          playing ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "playback-progress-front pointer-events-none absolute inset-0 rounded-full",
          playing ? "opacity-100" : "opacity-0",
        )}
      />
      <span aria-hidden="true" className="relative z-10 grid whitespace-nowrap">
        <span
          className={cn(
            "col-start-1 row-start-1",
            playing ? "invisible" : "visible",
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            "col-start-1 row-start-1",
            playing ? "visible" : "invisible",
          )}
        >
          {playingLabel ?? label}
        </span>
      </span>
    </button>
  );
};
